import type { Node, Edge } from '@xyflow/react';
import { isNodeWiredToLunaCore } from './pulseUtils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CANONICAL LUNA NODE REGISTRY & TOPOLOGY ENGINE
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Single authoritative source of truth for:
 * 1. Canonical node type normalization
 * 2. Exact handle & color matching for Auto-Wiring (Star Topology)
 * 3. Flow Health & Pre-flight Diagnostics
 * 4. Multi-Tenant lock governance schema definition
 * 
 * Conforms strictly to:
 * - AGENTS.md Mandate 16 (Canonical Luna Visual Builder & Jigsaw Architecture)
 * - AGENTS.md Mandate 17 (Strict New Node 8-Point Onboarding Contract)
 * - AGENTS.md Mandate 18 (Canonical Pulse Glow & Telemetry Standard)
 */

export type CanonicalNodeType =
  | 'core'
  | 'instruction'
  | 'memory'
  | 'callguard'
  | 'media'
  | 'knowledge'
  | 'scraper'
  | 'whatsapp'
  | 'payment'
  | 'transfer'
  | 'form'
  | 'typing'
  | 'database'
  | 'email'
  | 'authgateway'
  | 'authguard'
  | 'security'
  | 'section'
  | 'action'
  | 'intent'
  | 'catalog'
  | 'followup'
  | 'review_booster'
  | 'self_improve'
  | 'web_widget'
  | 'sip_call'
  | 'custom';

export interface WiringEdgeConfig {
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
  color: string;
}

export interface FlowHealthDiagnosticResult {
  score: number;
  status: 'healthy' | 'warning' | 'critical' | 'optimal';
  issues: string[];
  disconnectedCount: number;
  unwiredNodes: any[];
}

export interface NodeCategoryMetadata {
  canonicalType: CanonicalNodeType;
  displayName: string;
  category: 'core' | 'upstream' | 'downstream' | 'bidirectional';
  color: string;
  tailwindBorder: string;
  tailwindGlow: string;
  defaultData: Record<string, any>;
}

/**
 * Resolves any node object or identifier into its canonical type string.
 */
export function getCanonicalNodeType(node: { type?: string; id?: string } | null | undefined): CanonicalNodeType {
  if (!node) return 'custom';
  const t = (node.type || '').toLowerCase();
  const id = (node.id || '').toLowerCase();

  if (t === 'lunacorenode' || t === 'luna_core_hub' || id.includes('core') || t.includes('core')) return 'core';
  if (t.includes('callguard') || t.includes('call_guard') || id.includes('callguard') || id.includes('call_guard')) return 'callguard';
  if (t.includes('instruction') || t.includes('persona') || id.includes('instruction') || id.includes('persona')) return 'instruction';
  if (t.includes('memory') || id.includes('memory')) return 'memory';
  if (t.includes('mediavault') || t.includes('media_vault') || t.includes('media') || id.includes('mediavault') || id.includes('media_vault')) return 'media';
  if (t.includes('knowledge') || id.includes('knowledge')) return 'knowledge';
  if (t.includes('scraper') || t.includes('crawl') || id.includes('scraper')) return 'scraper';
  if (t.includes('whatsapp') || id.includes('whatsapp')) return 'whatsapp';
  if (t.includes('payment') || id.includes('payment')) return 'payment';
  if (t.includes('transfer') || id.includes('transfer')) return 'transfer';
  if (t.includes('form') || id.includes('form')) return 'form';
  if (t.includes('voice') || t.includes('typing') || id.includes('typing')) return 'typing';
  if (t.includes('database') || t.includes('crm') || t.includes('storage') || id.includes('database')) return 'database';
  if (t.includes('email') || t.includes('smtp') || id.includes('email')) return 'email';
  if (t.includes('authgateway') || id.includes('authgateway')) return 'authgateway';
  if (t.includes('authguard') || id.includes('authguard')) return 'authguard';
  if (t.includes('permission') || t.includes('security') || t.includes('rbac') || id.includes('permission') || id.includes('security')) return 'security';
  if (t.includes('section') || t.includes('route') || id.includes('section')) return 'section';
  if (t.includes('action') || id.includes('action')) return 'action';
  if (t.includes('intent') || id.includes('intent')) return 'intent';
  if (t.includes('catalog') || t.includes('product') || id.includes('catalog') || id.includes('product')) return 'catalog';
  if (t.includes('followup') || t.includes('follow_up') || id.includes('followup') || id.includes('follow_up')) return 'followup';
  if (t.includes('selfimprove') || t.includes('self_improve') || t.includes('self-improve') || id.includes('selfimprove') || id.includes('self_improve') || id.includes('self-improve')) return 'self_improve';
  if (t.includes('review') || t.includes('rating') || id.includes('review') || id.includes('rating')) return 'review_booster';
  if (t.includes('webwidget') || t.includes('web_widget') || t.includes('website') || id.includes('webwidget') || id.includes('web_widget')) return 'web_widget';
  if (t.includes('sipcall') || t.includes('sip_call') || t.includes('pbx') || id.includes('sipcall') || id.includes('sip_call') || id.includes('pbx')) return 'sip_call';

  const cleaned = t.replace(/node/g, '').replace(/_/g, '').trim();
  return (cleaned as CanonicalNodeType) || 'custom';
}

/**
 * Returns exact matching handles and colors for Auto-Wiring (Star Topology to Luna Core).
 * Supports bi-directional multi-edge nodes (WhatsApp Live Instance & SIP Calling).
 */
export function getWiringEdgeConfigs(node: Node | any, primaryCoreId: string): WiringEdgeConfig[] {
  const canon = getCanonicalNodeType(node);

  // 1. Bidirectional Nodes (both Inbound & Outbound connected to Luna Core)
  if (canon === 'whatsapp') {
    return [
      { source: primaryCoreId, sourceHandle: 'out-whatsapp', target: node.id, targetHandle: 'in-whatsapp', color: '#10b981' },
      { source: node.id, sourceHandle: 'out-whatsapp-instance', target: primaryCoreId, targetHandle: 'in-core', color: '#10b981' },
    ];
  }
  if (canon === 'sip_call') {
    return [
      { source: node.id, sourceHandle: 'out-sip-call', target: primaryCoreId, targetHandle: 'in-core', color: '#06b6d4' },
      { source: primaryCoreId, sourceHandle: 'out-sip-call', target: node.id, targetHandle: 'in-sip-call', color: '#06b6d4' },
    ];
  }

  // 2. Upstream Sensory / Guardrail Nodes: Source = Node (out-*), Target = Luna Core Hub (in-*)
  if (canon === 'catalog') {
    return [{ source: node.id, sourceHandle: 'out-catalog', target: primaryCoreId, targetHandle: 'in-knowledge', color: '#10b981' }];
  }
  if (canon === 'callguard') {
    return [{ source: node.id, sourceHandle: 'out-callguard', target: primaryCoreId, targetHandle: 'in-security', color: '#f59e0b' }];
  }
  if (canon === 'instruction') {
    return [{ source: node.id, sourceHandle: 'out-instruction', target: primaryCoreId, targetHandle: 'in-instructions', color: '#38bdf8' }];
  }
  if (canon === 'memory') {
    return [{ source: node.id, sourceHandle: 'out-memory', target: primaryCoreId, targetHandle: 'in-memory', color: '#c084fc' }];
  }
  if (canon === 'knowledge') {
    return [{ source: node.id, sourceHandle: 'out-knowledge', target: primaryCoreId, targetHandle: 'in-knowledge', color: '#10b981' }];
  }
  if (canon === 'media') {
    return [{ source: node.id, sourceHandle: 'out-media-vault', target: primaryCoreId, targetHandle: 'in-knowledge', color: '#10b981' }];
  }
  if (canon === 'scraper') {
    return [{ source: node.id, sourceHandle: 'out-scraper', target: primaryCoreId, targetHandle: 'in-scraper', color: '#6366f1' }];
  }
  if (canon === 'security' || canon === 'authguard') {
    return [{ source: node.id, sourceHandle: 'out-security', target: primaryCoreId, targetHandle: 'in-security', color: '#f59e0b' }];
  }
  if (canon === 'section') {
    return [{ source: node.id, sourceHandle: 'out-section', target: primaryCoreId, targetHandle: 'in-section-route', color: '#818cf8' }];
  }

  // 3. Downstream Action / Output Nodes: Source = Luna Core Hub (out-*), Target = Node (in-*)
  if (canon === 'transfer') {
    return [{ source: primaryCoreId, sourceHandle: 'out-whatsapp', target: node.id, targetHandle: 'in-transfer', color: '#10b981' }];
  }
  if (canon === 'action') {
    return [{ source: primaryCoreId, sourceHandle: 'out-actions', target: node.id, targetHandle: 'in-action', color: '#a855f7' }];
  }
  if (canon === 'form') {
    return [{ source: primaryCoreId, sourceHandle: 'out-actions', target: node.id, targetHandle: 'in-form-doc', color: '#a855f7' }];
  }
  if (canon === 'typing') {
    return [{ source: primaryCoreId, sourceHandle: 'out-actions', target: node.id, targetHandle: 'in-voice-typing', color: '#a855f7' }];
  }
  if (canon === 'database') {
    return [{ source: primaryCoreId, sourceHandle: 'out-actions', target: node.id, targetHandle: 'in-db', color: '#a855f7' }];
  }
  if (canon === 'email') {
    return [{ source: primaryCoreId, sourceHandle: 'out-actions', target: node.id, targetHandle: 'in-email', color: '#a855f7' }];
  }
  if (canon === 'authgateway') {
    return [{ source: primaryCoreId, sourceHandle: 'out-actions', target: node.id, targetHandle: 'in-auth', color: '#a855f7' }];
  }
  if (canon === 'payment') {
    return [{ source: primaryCoreId, sourceHandle: 'out-pos', target: node.id, targetHandle: 'in-payment', color: '#10b981' }];
  }
  if (canon === 'intent') {
    return [{ source: primaryCoreId, sourceHandle: 'out-intent', target: node.id, targetHandle: 'in-intent', color: '#f43f5e' }];
  }
  if (canon === 'followup') {
    return [{ source: primaryCoreId, sourceHandle: 'out-actions', target: node.id, targetHandle: 'in-followup', color: '#a855f7' }];
  }
  if (canon === 'review_booster') {
    return [{ source: primaryCoreId, sourceHandle: 'out-actions', target: node.id, targetHandle: 'in-review', color: '#a855f7' }];
  }
  if (canon === 'self_improve') {
    return [{ source: primaryCoreId, sourceHandle: 'out-actions', target: node.id, targetHandle: 'in-self-improve', color: '#38bdf8' }];
  }
  if (canon === 'web_widget') {
    return [{ source: primaryCoreId, sourceHandle: 'out-web-widget', target: node.id, targetHandle: 'in-web-widget', color: '#06b6d4' }];
  }

  return [{ source: primaryCoreId, sourceHandle: 'out-core', target: node.id, targetHandle: 'in-core', color: '#06b6d4' }];
}

/**
 * Shared Pre-Flight Flow Health Diagnostic Engine
 * Evaluates missing core nodes, unplugged nodes, and critical field validations across all active nodes.
 */
export function evaluateFlowHealthDiagnostics(
  nodes: Node[] | any[],
  edges: Edge[] | any[]
): FlowHealthDiagnosticResult {
  const coreNode = nodes.find(
    (n) => n.type === 'lunaCoreNode' || n.type === 'LUNA_CORE_HUB' || n.id === 'node-luna-core' || String(n.id).includes('core')
  );

  if (!coreNode) {
    return {
      score: 0,
      status: 'critical',
      issues: ['Missing Central Luna Core Hub Node'],
      disconnectedCount: nodes.length,
      unwiredNodes: nodes,
    };
  }

  const unwired = nodes.filter((n) => n.id !== coreNode.id && !isNodeWiredToLunaCore(n.id, edges, nodes));
  const issues: string[] = [];

  if (unwired.length > 0) {
    issues.push(`${unwired.length} node(s) unplugged from Luna Core Hub`);
  }

  for (const n of nodes) {
    const d: any = n.data || {};
    const cType = getCanonicalNodeType(n);

    if (cType === 'payment' && !d.accountNumber && !d.paymentLinkUrl && !d.bankName) {
      issues.push(`Payment Gateway (${n.id}) has no Bank Account or Link`);
    }
    if (cType === 'transfer' && !d.transferPhoneNumber) {
      issues.push(`Call Transfer (${n.id}) missing agent phone number`);
    }
    if (cType === 'intent') {
      const intentRules = Array.isArray(d.rules) ? d.rules : (d.rules === undefined ? [1] : []);
      if (intentRules.length === 0) {
        issues.push(`Smart Intent Alerts (${n.id}) has no monitored intent rules`);
      }
    }
    if (cType === 'catalog') {
      const prods = Array.isArray(d.products) ? d.products : (d.products === undefined ? [1] : []);
      if (prods.length === 0) {
        issues.push(`Product Catalog (${n.id}) has no items configured`);
      }
    }
    if (cType === 'followup' && d.isEnabled !== false && (!d.delayHours || d.delayHours < 1)) {
      issues.push(`Follow-Up Node (${n.id}) has invalid delay configured`);
    }
    if (cType === 'review_booster' && d.isEnabled !== false && !d.googleReviewUrl) {
      issues.push(`Review Booster (${n.id}) missing Google Review Link`);
    }
    if (cType === 'whatsapp' && d.apiMode === 'official' && ((d.useCustomMetaToken && !d.metaAccessToken) || !d.metaPhoneNumberId)) {
      issues.push(`WhatsApp Instance (${n.id}) official Meta mode missing ${d.useCustomMetaToken && !d.metaAccessToken ? 'Access Token or ' : ''}Phone ID`);
    }
    if (cType === 'email' && !d.smtpHost && !d.smtpUser) {
      issues.push(`Email SMTP (${n.id}) missing SMTP host or user`);
    }
    if (cType === 'scraper' && !d.targetUrl) {
      issues.push(`Web Scraper (${n.id}) missing target domain URL`);
    }
    if (cType === 'database' && !d.collectionName) {
      issues.push(`Database Storage (${n.id}) missing collection name`);
    }
    if (cType === 'web_widget' && d.isEnabled !== false && (!d.allowedDomains || (Array.isArray(d.allowedDomains) && d.allowedDomains.length === 0))) {
      issues.push(`Website Widget (${n.id}) has no allowed domains configured`);
    }
    if (cType === 'sip_call' && !d.inboundDidNumber && !d.pbxHost) {
      issues.push(`Enterprise SIP Phone (${n.id}) missing Landline Number or PBX Host`);
    }
  }

  const totalDeductions = (unwired.length * 15) + (issues.length * 5);
  const score = Math.max(0, 100 - totalDeductions);

  let status: FlowHealthDiagnosticResult['status'] = 'critical';
  if (score >= 95 && unwired.length === 0 && issues.length === 0) {
    status = 'healthy';
  } else if (score >= 60) {
    status = 'warning';
  }

  return {
    score,
    status,
    issues,
    disconnectedCount: unwired.length,
    unwiredNodes: unwired,
  };
}
